import { DocPage } from './docsTypes';

/**
 * Model Portfolios documentation.
 *
 * Formulas are transcribed from the implementation, not from a textbook — where
 * the code differs from the canonical definition (and in several places it
 * does), the code is what is documented, with the difference called out.
 */
export const modelPortfoliosDoc: DocPage = {
  id: 'model-portfolios',
  title: 'Model Portfolios',
  route: '/portfolio',
  intro:
    'A model portfolio is a named list of symbols with target weights — fractions that must sum to 1.0. ' +
    'There are no shares, no cost basis, no cash and no transactions. It records an allocation ("this should be 8% of the book"), ' +
    'not a holding ("I own 23 shares at $412"). Almost every strength and limitation below follows from that one design choice.',
  sections: [
    {
      id: 'mp-core',
      title: 'The core object',
      summary: 'Name, optional description/tags/region, and a set of holdings — each a symbol, a target weight, and a role.',
      useCase:
        'Use one per strategy or mandate you want to track separately: a core long-term book, a thematic sleeve, ' +
        'a paper portfolio you are testing. Analytics, benchmarking and attribution all run per portfolio.',
      howTo: [
        'Portfolio → Model Portfolios → New Portfolio. Name is required and must be unique.',
        'Add holdings by symbol and type a target weight for each. Weights are percentages in the UI, stored as fractions.',
        'Save. The API rejects the save unless the weights sum to 100% (±2%) and every weight is greater than zero.',
        'Optionally set a region (US/UK/EU/SWISS/SINGAPORE/KOREA/GLOBAL/THEMATIC) — it is auto-detected from holding currencies if left blank.',
      ],
      terms: [
        {
          term: 'Target weight',
          definition: 'The fraction of the portfolio a symbol is intended to represent. Stored 0–1, displayed as a percentage.',
          reading: 'It is an intention, not a measurement. Nothing in the app compares it to what you actually own.',
        },
        {
          term: 'Role (core / satellite)',
          definition: 'A label on each holding. Set on save, defaults to "core".',
          reading: 'Descriptive only — no calculation treats core and satellite differently.',
        },
        {
          term: 'Rebalance history',
          definition:
            'Every save writes an immutable JSON snapshot of the holdings to model_portfolio_rebalances, tagged manual or recommendation.',
          reading:
            'A pure audit log. It is never diffed against current holdings and never drives a calculation — it answers "what did this look like in March", nothing more.',
        },
      ],
      caveats: [
        'The ±2% weight-sum rule is the only constraint enforced anywhere in the portfolio surface. A single holding at 100% saves cleanly.',
        'There is no link between a model portfolio and the Tracker. They share no key, so "what I intend" and "what I own" are unrelated records.',
      ],
    },
    {
      id: 'mp-returns',
      title: 'Return metrics',
      summary: 'Computed from daily closes in the database, weighted by target weight, over the selected timeframe.',
      useCase: 'Answering "how would this allocation have performed", assuming it was held at constant weights throughout.',
      terms: [
        {
          term: 'Portfolio daily return',
          definition: 'The weighted sum of each holding\'s daily return, using normalised target weights.',
          reading:
            'This assumes continuous rebalancing back to target every single day. A real buy-and-hold portfolio drifts away from its weights, so live results will differ — usually the backtest looks better, because daily rebalancing quietly harvests mean reversion.',
        },
        {
          term: 'Timeframe',
          definition: '5D, 10D, 20D, 30D/1M, 3M, 6M, 1Y, 2Y or 5Y of calendar days of history.',
          reading: 'If the database lacks enough history for a symbol, it is backfilled from yfinance so long windows still work.',
        },
      ],
      formulas: [
        {
          name: 'Total return',
          expression: 'Π(1 + rₜ) − 1',
          reasoning:
            'Geometric compounding of daily returns, not a simple sum. Compounding matters: −50% then +50% is −25%, not 0%.',
          source: 'model_portfolio_service.py — _calculate_risk_metrics',
        },
        {
          name: 'Annualised return',
          expression: 'mean(rₜ) × 252',
          reasoning:
            'The arithmetic mean daily return scaled by 252 trading days. Note this is the arithmetic annualisation, which always exceeds CAGR when returns vary — both are shown, and CAGR is the one to quote.',
        },
        {
          name: 'CAGR',
          expression: '(1 + total_return)^(1 / years) − 1,   years = n_days / 252',
          reasoning:
            'The constant annual growth rate that would produce the same ending value. The honest headline number, because it accounts for the path.',
        },
      ],
    },
    {
      id: 'mp-risk',
      title: 'Risk metrics',
      summary: 'Volatility, drawdown and tail-loss measures from the same daily return series.',
      useCase: 'Sizing a position, comparing two allocations that have similar returns, or understanding what a bad month looks like.',
      terms: [
        {
          term: 'Annualised volatility',
          definition: 'Standard deviation of daily returns × √252.',
          reading:
            'Roughly the range that contains two thirds of annual outcomes. 15% volatility means a year inside ±15% about two times in three. It treats upside and downside identically, which is why Sortino exists.',
        },
        {
          term: 'Max drawdown',
          definition: 'The largest peak-to-trough decline over the window.',
          reading:
            'The single most behaviourally relevant number here — it is the loss you would have had to sit through without selling.',
        },
        {
          term: 'Max drawdown duration',
          definition: 'The longest unbroken run of days spent below a previous peak.',
          reading: 'Depth is one thing; two years underwater is another. This measures the second.',
        },
        {
          term: 'VaR 95 / 99',
          definition: 'The 5th and 1st percentile of daily returns.',
          reading: 'VaR 95 of −2% means: on the worst 1 day in 20, you lost at least 2%. It says nothing about how much worse it got.',
        },
        {
          term: 'CVaR 95 / 99',
          definition: 'The mean of all returns at or below the corresponding VaR. Also called Expected Shortfall.',
          reading:
            'The average of the bad tail, which is what VaR omits. Two portfolios can share a VaR while one has a far heavier tail — CVaR separates them.',
        },
        {
          term: 'Skewness / Kurtosis',
          definition: 'Third and fourth moments of the daily return distribution.',
          reading:
            'Negative skew = occasional large losses against frequent small gains. High kurtosis = fat tails, so extreme days are more common than a normal distribution implies. Both warn that volatility alone is understating risk.',
        },
      ],
      formulas: [
        {
          name: 'Annualised volatility',
          expression: 'std(rₜ, ddof=1) × √252',
          reasoning:
            'Sample standard deviation (ddof=1, dividing by n−1) because these are a sample of returns, not the full population. √252 is the square-root-of-time rule, which assumes returns are independent day to day — an approximation that understates risk when volatility clusters, as it does in practice.',
        },
        {
          name: 'Drawdown series',
          expression: 'cum = Π(1 + rₜ);   dd = (cum − cummax(cum)) / cummax(cum)',
          reasoning:
            'Running the cumulative curve against its own running maximum, so every point is measured against the best value seen so far rather than the start.',
        },
        {
          name: 'Downside deviation',
          expression: 'std(excessₜ where excessₜ < 0, ddof=1) × √252,   excessₜ = rₜ − rf/252',
          reasoning:
            'Volatility of the below-risk-free returns only. Upside dispersion is not risk, and penalising it treats a strong month as a problem.',
        },
        {
          name: 'CVaR 95',
          expression: 'mean(rₜ | rₜ ≤ VaR₉₅),   VaR₉₅ = percentile(rₜ, 5)',
          reasoning:
            'A historical (non-parametric) estimate — it uses the actual observed tail rather than assuming a normal distribution, which would badly understate it given the kurtosis usually present.',
        },
      ],
      caveats: [
        'All of these are backward-looking and computed at constant weights. They describe the window you selected, not the future.',
        'The √252 scaling assumes day-to-day independence. Real markets cluster volatility, so annualised figures understate risk in stressed periods.',
      ],
    },
    {
      id: 'mp-risk-adjusted',
      title: 'Risk-adjusted ratios',
      summary: 'Return per unit of risk, with each ratio using a different definition of "risk".',
      useCase: 'Comparing allocations with different volatility. A higher return at proportionally higher risk is not an improvement.',
      formulas: [
        {
          name: 'Sharpe ratio',
          expression: '(annualised return − rf) / annualised volatility',
          reasoning:
            'Excess return per unit of total volatility. The default comparator, but it penalises upside volatility equally with downside, and assumes roughly symmetric returns.',
        },
        {
          name: 'Sortino ratio',
          expression: '(annualised return − rf) / downside deviation',
          reasoning:
            'Sharpe with only harmful volatility in the denominator. Usually the fairer measure for anything with positive skew or an options-like payoff.',
        },
        {
          name: 'Calmar ratio',
          expression: 'annualised return / |max drawdown|',
          reasoning:
            'Return per unit of worst historical loss. Note this implementation uses raw annualised return, not excess over the risk-free rate.',
        },
        {
          name: 'Treynor ratio',
          expression: '(annualised return − rf) / β',
          reasoning:
            'Excess return per unit of market risk rather than total risk. Meaningful only if the portfolio is well diversified, so that idiosyncratic risk has largely been removed.',
        },
        {
          name: 'Information ratio',
          expression: '(portfolio annualised return − benchmark annualised return) / tracking error',
          reasoning:
            'Active return per unit of active risk — the measure of whether deviating from the benchmark is paying for itself.',
        },
      ],
      terms: [
        {
          term: 'Risk-free rate (rf)',
          definition:
            'A weighted 10-year sovereign yield, blended across the countries the holdings are domiciled in.',
          reading:
            'Live per-country, sourced from Bundesbank, Bank of England, Japan MOF, Bank of Canada, ECB and FRED depending on the market — not a hardcoded constant.',
        },
      ],
    },
    {
      id: 'mp-benchmark',
      title: 'Benchmark comparison',
      summary: 'Alpha, beta, R², tracking error and correlation against up to 116 selectable benchmarks.',
      useCase: 'Separating "the market went up" from "this allocation did something".',
      howTo: [
        'Open a portfolio → Performance or Risk Analytics.',
        'Set benchmarks via the portfolio\'s benchmark selector; several can be compared at once.',
      ],
      formulas: [
        {
          name: 'Beta',
          expression: 'cov(r_portfolio, r_benchmark) / var(r_benchmark)',
          reasoning:
            'Sensitivity to the benchmark. β = 1.2 means the portfolio has historically moved 1.2% for each 1% benchmark move.',
        },
        {
          name: 'Alpha (Jensen\'s)',
          expression: 'r_p − [rf + β × (r_b − rf)]',
          reasoning:
            'Return beyond what beta exposure alone would have produced. This is the CAPM definition — it is only as meaningful as beta is stable, so read it alongside R².',
        },
        {
          name: 'R²',
          expression: 'corr(r_p, r_b)²',
          reasoning:
            'The share of portfolio movement explained by the benchmark. Low R² makes alpha and beta unreliable, because you are regressing against something that does not explain much.',
        },
        {
          name: 'Tracking error',
          expression: 'std(r_p − r_b, ddof=1) × √252',
          reasoning: 'Volatility of the active return. How far this can wander from the benchmark in a year.',
        },
      ],
    },
    {
      id: 'mp-contribution',
      title: 'Per-holding contribution',
      summary: 'Splits portfolio return and portfolio risk across the individual holdings.',
      useCase:
        'Finding the position that is quietly supplying most of your volatility. This is the most actionable table in the analytics tab.',
      formulas: [
        {
          name: 'Return contribution',
          expression: 'annualised return of the symbol × its normalised weight',
          reasoning: 'Additive across holdings — they sum to the portfolio return.',
        },
        {
          name: 'Risk contribution',
          expression: 'wᵢ × cov(rᵢ, r_portfolio) × 252 / σ_portfolio',
          reasoning:
            'Marginal contribution to total risk. Crucially it uses covariance with the whole portfolio, not the symbol\'s own volatility — a volatile holding that is uncorrelated with everything else can contribute very little risk, while a moderately volatile one that moves with everything contributes a lot. These also sum to the portfolio volatility.',
        },
      ],
      terms: [
        {
          term: 'Risk contribution vs weight',
          definition: 'The gap between how much of the portfolio a position is, and how much of the risk it supplies.',
          reading:
            'A 5% position contributing 20% of risk is the signal to act on. The Recommendations engine turns exactly this ratio into its risk factor.',
        },
      ],
    },
    {
      id: 'mp-score',
      title: 'Portfolio score',
      summary: 'A diversification score from correlations, plus concentration measures.',
      formulas: [
        {
          name: 'Diversification score',
          expression: 'max(0, 1 − average pairwise correlation) × 100',
          reasoning:
            'Averaged over the upper triangle of the correlation matrix, excluding the diagonal. 100 means uncorrelated holdings; 0 means they all move together.',
        },
        {
          name: 'HHI (Herfindahl-Hirschman Index)',
          expression: 'Σ wᵢ²',
          reasoning:
            'The standard concentration measure. Equal weights across n holdings give 1/n; a single position gives 1.',
        },
        {
          name: 'Concentration score',
          expression: 'max(0, 1 − (HHI − 1/n) / (1 − 1/n)) × 100',
          reasoning:
            'HHI normalised so that equal-weight always scores 100 and a single holding scores 0, regardless of how many holdings there are. Without the normalisation, raw HHI is not comparable between portfolios of different sizes.',
        },
      ],
      caveats: [
        'The headline "Diversification" number is correlation-only and completely blind to position sizing. A portfolio 60% in one name scores identically to an equal-weighted one if the correlations match.',
        'Concentration score and HHI are computed and returned by the API but are not rendered anywhere in the UI. The measure that actually captures sizing is the one you cannot see.',
      ],
    },
    {
      id: 'mp-attribution',
      title: 'Attribution (Brinson)',
      summary: 'Splits active return into allocation, selection and interaction effects by sector.',
      useCase:
        'Answering the question performance alone cannot: did you win because you were overweight the right sector, or because you picked better names inside it?',
      formulas: [
        {
          name: 'Allocation effect',
          expression: '(wₚ − w_b) × (r_b,sector − r_b,total)',
          reasoning:
            'The payoff from sector weighting alone. Positive when you were overweight a sector that beat the benchmark overall — a sector-timing result, independent of which names you held.',
        },
        {
          name: 'Selection effect',
          expression: 'w_b × (rₚ,sector − r_b,sector)',
          reasoning:
            'The payoff from stock picking within a sector, held at benchmark weight so it is not contaminated by the allocation decision.',
        },
        {
          name: 'Interaction effect',
          expression: '(wₚ − w_b) × (rₚ,sector − r_b,sector)',
          reasoning:
            'The cross term: being overweight a sector *and* picking well within it. Reported separately rather than folded into selection, because combining them flatters the picking.',
        },
      ],
      caveats: [
        'Benchmark sector returns are proxied by sector SPDR ETFs (XLK, XLF, XLV…), not by the benchmark\'s actual sector constituents.',
        'If a benchmark has no stored sector weights, it falls back to assuming uniform weights across the sectors you happen to hold — which makes the allocation effect close to meaningless. Check the benchmark is one with real weights before reading it.',
        'Holdings whose sector cannot be mapped to an ETF (e.g. "Unknown") are dropped from the analysis entirely.',
      ],
    },
    {
      id: 'mp-frontier',
      title: 'Efficient Frontier',
      summary: 'Six optimised weight vectors plus the full risk/return frontier, with a shrunk covariance estimate and an adjustable position cap.',
      useCase:
        'Sanity-checking your weights against what an optimiser would choose, and seeing how much of your risk is avoidable through weighting alone.',
      howTo: [
        'Open a portfolio → Efficient Frontier.',
        'Choose the estimation window (6M/1Y/2Y/5Y) and a risk-free rate source.',
        'Drag the Max position slider and release — it recalculates on release, because each change re-runs six optimisations plus a 100-point frontier.',
        'Click a strategy card or its point on the chart to see the per-symbol allocation.',
      ],
      terms: [
        {
          term: 'Efficient frontier',
          definition:
            'The set of portfolios with the highest achievable return for each level of risk. The chart draws the full curve, including the inefficient lower branch.',
        },
        {
          term: 'Risk Parity',
          definition: 'Weights chosen so every holding contributes equally to total portfolio risk.',
          reading: 'Needs no return forecast. The most robust of the six and the default selection.',
        },
        {
          term: 'Min Variance',
          definition: 'The single lowest-volatility combination available.',
          reading: 'No return forecast needed. Tends to concentrate in low-volatility names.',
        },
        {
          term: 'Min CVaR',
          definition: 'Minimises the average loss in the worst 5% of days rather than overall volatility.',
          reading: 'Targets tail risk specifically. Useful when the return distribution is skewed.',
        },
        {
          term: 'Max Diversification',
          definition: 'Maximises the ratio of weighted-average asset volatility to portfolio volatility.',
          reading: 'Explicitly seeks the largest diversification benefit rather than the lowest risk.',
        },
        {
          term: 'Inverse Volatility',
          definition: 'Weight proportional to 1/σᵢ.',
          reading: 'A simple heuristic that ignores correlations entirely — a rough cousin of risk parity.',
        },
        {
          term: 'Max Sharpe',
          definition: 'Maximises (return − rf)/σ.',
          reading:
            'The only one of the six needing expected returns, which are estimated here as the trailing 1-year mean. Marked FORECAST in the UI and deliberately listed last — see the caveat below.',
        },
      ],
      formulas: [
        {
          name: 'Ledoit-Wolf covariance shrinkage',
          expression: 'Σ̂ = δ·F + (1 − δ)·S,   F = (trace(S)/p)·I',
          reasoning:
            'The sample covariance S has biased extreme eigenvalues — the smallest too small, the largest too large — and an optimiser hunts precisely those extremes, which is much of why unconstrained Markowitz output swings between runs. Shrinking toward a scaled identity F by an analytically optimal intensity δ trades a little bias for a large variance reduction. δ is reported in the UI: 0 means the sample was kept as-is, 1 means it was fully replaced.',
          source: 'covariance.py — ledoit_wolf_shrinkage',
        },
        {
          name: 'Position cap feasibility floor',
          expression: 'effective cap = max(requested cap, 1/n)',
          reasoning:
            'A long-only fully-invested portfolio cannot hold n assets all below 1/n. The slider will not go below that floor, so you cannot select a cap that silently does not apply.',
        },
        {
          name: 'Risk parity objective',
          expression: 'minimise Σᵢ (RCᵢ − σₚ/n)²,   RCᵢ = wᵢ·(Σw)ᵢ / σₚ',
          reasoning:
            'Drives every holding\'s risk contribution toward an equal share. Solved numerically with SLSQP; there is no closed form under long-only constraints.',
        },
      ],
      caveats: [
        'Max Sharpe depends on expected returns estimated from a trailing 1-year mean. That input is famously unstable — measured on a real 8-stock book, Max Sharpe turns over roughly 29% of the portfolio when the estimation window shifts by six months, versus about 4% for Risk Parity. Treat it as illustrative, not as a recommendation.',
        'There is deliberately no "apply these weights" button. Transcribing them by hand is friction on purpose — a single click replacing a considered allocation with a curve-fit to trailing data is where this kind of tool starts doing harm.',
        'If an optimiser fails to converge it falls back to equal weight and the card is flagged with a warning icon, so a fallback is never silently presented as a genuine solve.',
      ],
    },
    {
      id: 'mp-montecarlo',
      title: 'Monte Carlo',
      summary: 'Simulates 10,000 forward paths from the holdings\' historical means and covariance.',
      useCase: 'Framing a range of outcomes over a multi-year horizon rather than a single projected number.',
      formulas: [
        {
          name: 'Correlated path generation',
          expression: 'r_t = μ + L·z_t,   where L = cholesky(Σ),  z_t ~ N(0, I)',
          reasoning:
            'The Cholesky factor L transforms independent normal draws into draws with the observed covariance structure. Without it, simulated holdings would move independently and the portfolio would look far better diversified than it is.',
        },
        {
          name: 'Portfolio value path',
          expression: 'V_t = V₀ × Π(1 + wᵀr_t)',
          reasoning: 'Compounds the weighted daily return across 252 × horizon_years steps, per simulation.',
        },
      ],
      terms: [
        {
          term: 'Percentile bands',
          definition: 'The 5th, 25th, 50th, 75th and 95th percentile of simulated value at each future date.',
          reading: 'The 5–95 band is a 90% confidence interval under the model\'s assumptions — not under reality.',
        },
        {
          term: 'Probability of loss',
          definition: 'The share of simulations ending below the starting value.',
        },
      ],
      caveats: [
        'Returns are drawn from a multivariate normal. Real returns have fat tails and volatility clustering, so the worst simulated outcomes are milder than the worst real ones.',
        'Means and covariances are assumed constant for the whole horizon, and are estimated from the same historical window that produced them.',
        'The output is a property of the model, not a forecast. Its value is in the shape of the range, not the numbers at the edges.',
      ],
    },
    {
      id: 'mp-recommendations',
      title: 'Trade recommendations',
      summary: 'Scores every holding on seven weighted factors and suggests a weight adjustment.',
      useCase: 'A structured second opinion on which positions to add to or trim — a prompt to look, not an instruction.',
      formulas: [
        {
          name: 'Composite score',
          expression:
            'signal×0.20 + momentum×0.15 + trend×0.15 + fundamentals×0.15 + risk×0.15 + P/E×0.10 + drift×0.10',
          reasoning:
            'Each sub-score runs −100 to +100 and the composite is clamped to the same range. The weights are a judgement call, not an estimated model — they were chosen, not fitted.',
        },
        {
          name: 'Suggested weight',
          expression: 'clamp(target_weight + (composite/100 × 5)/100,  0.01,  0.50), then renormalised to 100%',
          reasoning:
            'A maximum nudge of ±5 percentage points, so a single strong score cannot restructure the book. The 1% floor and 50% ceiling are fixed guardrails.',
        },
        {
          name: 'Momentum sub-score (RSI)',
          expression:
            'RSI > 70:  −50 − (RSI−70)×(50/30)    ·    RSI < 30:  +50 + (30−RSI)×(50/30)    ·    otherwise: (RSI−50)×2.5',
          reasoning:
            'Mean-reverting by construction: overbought scores negative, oversold positive. This is a contrarian assumption and will fight a strong trend.',
        },
        {
          name: 'Trend sub-score',
          expression: '±50 for above/below the 200-day MA,  ±25 for the 50-day MA',
          reasoning:
            'Trend-following, and therefore deliberately in tension with the momentum factor above. The 200-day is weighted double as the longer-term signal.',
        },
        {
          name: 'Risk sub-score',
          expression:
            'ratio = risk contribution / weight.   ratio > 1.5: −50 − min(50, (ratio−1.5)×50)   ·   ratio < 1: +50   ·   otherwise: −(ratio−1)×100',
          reasoning:
            'Penalises holdings supplying more risk than their weight justifies. The most defensible factor of the seven, because it is measured rather than assumed.',
        },
      ],
      terms: [
        {
          term: 'Action buckets',
          definition: 'Composite ≥ 60 Strong Add · ≥ 20 Add · −20 to 20 Hold · ≤ −20 Trim · ≤ −60 Strong Trim.',
        },
      ],
      caveats: [
        'The Weight Drift factor (10% of the composite) is permanently zero. "Current weight" is derived from target weights re-normalised by their own sum, so it compares target against target. Since the API enforces the sum, drift is always ≈0 — the factor contributes nothing and two related portfolio insights can never fire.',
        'The momentum factor is contrarian and the trend factor is trend-following. They will frequently cancel, which is a design tension rather than a bug, but it means a mid-range composite can hide two strong opposing signals.',
        'The UI methodology text states a ±10 percentage point adjustment; the implementation uses ±5.',
      ],
    },
    {
      id: 'mp-universe',
      title: 'Universe views',
      summary: 'Signals, Earnings, Movers, Radar and News across every symbol in every model portfolio at once.',
      useCase: 'A daily sweep of everything you have an allocation to, without opening each portfolio.',
      terms: [
        { term: 'Signals', definition: 'Technical signals (MA crosses, 52-week highs/lows, RSI extremes, momentum) on portfolio names.' },
        { term: 'Earnings', definition: 'Upcoming and recently reported earnings dates for holdings.' },
        { term: 'Movers', definition: 'Largest daily moves within the portfolio universe.' },
        { term: 'Radar', definition: 'Momentum ranking across the universe.' },
        { term: 'News', definition: 'Nightly-fetched articles for holdings.' },
      ],
      caveats: [
        'These views span all portfolios at once — they are not filtered to the portfolio you happen to have open.',
      ],
    },
  ],
};
